/**
 * BL-1288 (ADR 033 Decision 7) — the CLIENT-SAFE auction transition catalog: the
 * auction twin of {@link ../lot-transition-catalog}, carrying the operator-facing
 * metadata every consumer reads and no server code whatsoever.
 *
 * **Why this lands here rather than in BL-1289.** The matrix generator needs a
 * catalog per entity to enumerate — "every transition, none silently omitted" is
 * only checkable against a TOTAL record. `canApplyAuctionTransition` decided most
 * of the catalog when this file was written (BL-1286) and decides all of it now
 * (BL-1292), so the data existed; what was missing was the client-safe surface
 * naming them. BL-1289 (effects) and BL-1301 (the in-app reference page) both
 * consume this.
 *
 * **`from` is cross-checked, not transcribed.**
 * `__tests__/auction-transition-catalog.test.ts` replays every `live` entry
 * through `canApplyAuctionTransition` across the whole {@link AUCTION_PHASE_KINDS}
 * space, so `from` encodes **what the guard decides today** — the honest
 * client-side answer — rather than what the matrix aspires to. Where the two
 * differ, the divergence is noted on the entry.
 *
 * Source of truth for the values: `docs/feature/admin/lifecycle-transition-matrix.md` §4.
 */
import type { AuctionTransition } from './auction-transition-guard';
import type { AuctionPhaseKind } from './phase-tokens';
import type { TransitionStatus } from './lot-transition-catalog';

/** The operator-facing, client-safe description of one auction lifecycle transition. */
export interface AuctionTransitionMeta {
  /** The catalog id. Matches this entry's key in {@link AUCTION_TRANSITION_CATALOG}. */
  readonly id: AuctionTransition;
  /**
   * The operator-facing name. THE only sanctioned source of this copy — the
   * same rule the lot catalog's `operatorLabel` carries.
   *
   * BL-1563 reconciled the last "Ready" label with the operator-facing
   * vocabulary: BL-1129 renamed the phase to **Upcoming** across the admin
   * experience, and `mark-ready`'s label was the string that rename missed —
   * the dropdown row said "Upcoming" while the confirm button said "Mark
   * ready". Matrix §6 (Messaging) owns that copy; §7 carries the re-sign.
   *
   * Read this through {@link resolveAuctionOperatorLabel}, never directly, so
   * a per-source override is not silently skipped.
   */
  readonly operatorLabel: string;
  /**
   * Per-source-phase overrides of {@link operatorLabel}. Absent on every entry
   * but `mark-ready`, whose two directions are genuinely different operations:
   * from `draft` it PUBLISHES an auction, from anywhere else it pulls a
   * running one back.
   *
   * Data, not a function, deliberately: the catalog stays a pure literal that
   * `transition-matrix-doc.ts` can enumerate into the signed matrix and that
   * `__tests__/auction-transition-catalog.test.ts` can replay against the
   * guard. A function would defeat both.
   */
  readonly operatorLabelByFrom?: Partial<Record<AuctionPhaseKind, string>>;
  /** Whether the guard decides this transition today. */
  readonly status: TransitionStatus;
  /** Source phases the transition may be applied from. Empty for `planned` rows. */
  readonly from: readonly AuctionPhaseKind[];
  /**
   * Target phase(s). Non-empty on every entry — BL-1291 added
   * `accepting-pre-bids`, the last destination that had no
   * {@link AuctionPhaseKind} to name, so the "empty for the two pre-bidding
   * transitions" carve-out is retired.
   */
  readonly to: readonly AuctionPhaseKind[];
  /** True when the transition needs operator input before it can run (opens a form). */
  readonly needsForm: boolean;
  /**
   * The source phases from which this row launches an operation the transition
   * catalog does NOT own (matrix §2, row type 3). Empty ⇒ never fenced.
   *
   * Empty on EVERY auction entry: matrix §5's fenced operations (hammer-price
   * correction, Reverse Sale, swap buyer, lot-number publishing) are all
   * lot-level. The field exists so both catalogs read alike for BL-1292's
   * dropdown and BL-1301's reference page — which is why BL-1428's rename from
   * `isFenced: boolean` (needed on the lot side, where M4 fences per SOURCE
   * phase) is mirrored here even though no auction row uses it.
   */
  readonly fencedFrom: readonly AuctionPhaseKind[];
}

/**
 * Every auction lifecycle transition. TOTAL record — a new
 * {@link AuctionTransition} fails to compile until it is declared here AND
 * classified in the test's `EXPECTED_STATUS` table.
 */
export const AUCTION_TRANSITION_CATALOG: Record<AuctionTransition, AuctionTransitionMeta> = {
  // ── live: decided by `canApplyAuctionTransition` today ────────────────────
  'mark-draft': {
    id: 'mark-draft',
    operatorLabel: 'Move back to draft',
    status: 'live',
    // Matrix §4.1: every phase but `draft` itself (a no-op) and `deleted` —
    // which is what put `accepting-pre-bids` here in BL-1291 (AC 3).
    from: ['accepting-pre-bids', 'ready', 'active', 'paused', 'closed'],
    to: ['draft'],
    needsForm: false,
    fencedFrom: [],
  },
  'mark-ready': {
    id: 'mark-ready',
    // BL-1563 AC 4: the BASE is the backward reading, because five of the six
    // sources are backward moves. AC 3's forward reading is the override.
    operatorLabel: 'Move back to Upcoming',
    operatorLabelByFrom: { draft: 'Mark Upcoming' },
    status: 'live',
    // Matrix §4.2. The go-live gate (`readyBlockerCount`) gates this too, but
    // that is a precondition on live data, not a phase — `from` is the phase
    // axis only, which is exactly what the cross-check test replays.
    // BL-1291: from `accepting-pre-bids` this IS the stop-pre-bidding move —
    // one auction-level write that darkens every lot, touching no lot row.
    from: ['draft', 'accepting-pre-bids', 'active', 'paused', 'closed'],
    to: ['ready'],
    needsForm: false,
    fencedFrom: [],
  },
  open: {
    id: 'open',
    operatorLabel: 'Open auction',
    status: 'live',
    // Matrix §4.4. BL-1291 added `accepting-pre-bids`: opening for bidding is
    // what ENDS pre-bidding — there is no separate "stop pre-bidding" step (AC 2).
    from: ['ready', 'accepting-pre-bids'],
    to: ['active'],
    needsForm: false,
    fencedFrom: [],
  },
  pause: {
    id: 'pause',
    operatorLabel: 'Pause',
    status: 'live',
    from: ['active'],
    to: ['paused'],
    needsForm: false,
    fencedFrom: [],
  },
  resume: {
    id: 'resume',
    operatorLabel: 'Resume',
    status: 'live',
    from: ['paused'],
    to: ['active'],
    // Matrix §4.6's optional "extend close times by the pause duration" is a
    // choice ON the confirmation, not a form that gates the transition —
    // `resume` runs without it. Only `restore` cannot proceed without input.
    needsForm: false,
    fencedFrom: [],
  },
  close: {
    id: 'close',
    operatorLabel: 'Close auction',
    status: 'live',
    // Matrix §4.7 — every live phase (BL-2278 added the three pre-open ones, the
    // recovery path for a Closed auction walked back). Invariant I1 (no open
    // lots) is a live-data precondition, not a phase, so it is not encoded here.
    from: ['draft', 'ready', 'accepting-pre-bids', 'active', 'paused'],
    to: ['closed'],
    needsForm: false,
    fencedFrom: [],
  },
  'set-close-stage': {
    id: 'set-close-stage',
    operatorLabel: 'Set closing stage',
    status: 'live',
    // Matrix §4.7b (BL-2279, PRJ-89) — closed → closed. The stage is derived
    // from the four stage stamps (`deriveCloseStage`), never a phase, so the
    // auction stays `closed` at every stage. `needsForm` because the operator
    // picks the target stage; the guard's stage params (current vs target) are
    // live data the catalog replay does not see, so `from` is the phase axis.
    from: ['closed'],
    to: ['closed'],
    needsForm: true,
    fencedFrom: [],
  },
  reopen: {
    id: 'reopen',
    operatorLabel: 'Reopen',
    status: 'live',
    // Matrix §4.8 — un-close, change nothing else. BL-1516 retired `post-sale`,
    // which used to name this destination; `reopen` still writes exactly one
    // thing (clear `closed_at`) and the resulting derivation IS `active`, so the
    // cell moved in name only. No lot row and no bid is touched (matrix §7w).
    from: ['closed'],
    to: ['active'],
    needsForm: false,
    fencedFrom: [],
  },
  'soft-delete': {
    id: 'soft-delete',
    operatorLabel: 'Delete',
    status: 'live',
    // Matrix §4.9 — any non-`deleted` phase. The `SoftDeleteService` restrict
    // blockers are a live-data precondition the guard takes as an input.
    from: ['draft', 'accepting-pre-bids', 'ready', 'active', 'paused', 'closed'],
    to: ['deleted'],
    needsForm: false,
    fencedFrom: [],
  },
  restore: {
    id: 'restore',
    operatorLabel: 'Restore',
    status: 'live',
    from: ['deleted'],
    // Matrix §4.10: the operator chooses the target state, and the grid's
    // Deleted row offers exactly these three (mirrored by
    // `AUCTION_RESTORE_TARGETS`). `active` is excluded by I4b — the lots decide
    // it. (BL-1516: `post-sale` was excluded for the same reason and is gone.)
    // `accepting-pre-bids` is excluded for the §4.3b reason: `restore` writes auction stamps and no lot row, so it cannot
    // enrol the lots that phase needs.
    to: ['draft', 'ready', 'closed'],
    needsForm: true,
    fencedFrom: [],
  },

  // ── the two pre-bidding entries — live as of BL-1292 ──────────────────────
  // These were the catalog's last `planned` rows. BL-1291 gave them a real
  // DESTINATION (`accepting-pre-bids`); BL-1292 declared their effects on the
  // transition engine and implemented their guard cells, so both are now `live`
  // with `from` sets the catalog↔guard agreement test replays like any other.
  //
  // That matters beyond bookkeeping: `LIVE_AUCTION_TRANSITIONS` filters on
  // `status`, so while these were `planned` the status dropdown could not offer
  // pre-bidding at all, and the settings control had to answer for
  // `accepting-pre-bids` by bypassing the guard. Flipping them live is what let
  // that second decision-maker be deleted (rule #38 §2).
  'open-pre-bidding': {
    id: 'open-pre-bidding',
    operatorLabel: 'Open for pre-bidding',
    status: 'live',
    // Matrix §4.3. `ready` alone — the transition sweeps every `ready` lot, so
    // it only makes sense where such a set exists. Its "≥1 eligible lot"
    // precondition is live data, not a phase, so (like I1 and the go-live gate)
    // it is not encoded here.
    from: ['ready'],
    to: ['accepting-pre-bids'],
    needsForm: false,
    fencedFrom: [],
  },
  'mark-pre-bidding': {
    id: 'mark-pre-bidding',
    // Matrix §4.3b — the BACKWARD entry: stamps the auction only, touches no
    // lot. The label has to read differently from `open-pre-bidding` or the
    // dropdown offers two rows that look like the same action.
    operatorLabel: 'Move back to pre-bidding',
    status: 'live',
    // Matrix §4.3b — the two RUNNING phases. `draft` is excluded ("use
    // mark-ready + open-pre-bidding") and `ready` is excluded because from there
    // the forward move IS `open-pre-bidding`.
    //
    // BL-1559 (§7o) narrowed this from ['active','paused','post-sale','closed']
    // (`post-sale` itself retired by BL-1516).
    // While `post-sale` and `closed` were sources, Closed → Accepting pre-bids
    // was one dropdown pick that consulted NO gate whatsoever — a finished
    // auction could start taking real advance bids with a historical schedule or
    // no terms attached. Keep this in lockstep with `MARK_PRE_BIDDING_SOURCES`
    // in auction-transition-guard.ts; the catalog↔guard agreement test is what
    // catches a one-sided edit.
    from: ['active', 'paused'],
    to: ['accepting-pre-bids'],
    needsForm: false,
    fencedFrom: [],
  },
};

/**
 * The transitions the guard decides today, for consumers that must not offer an
 * unbuilt row (BL-1292's auction status dropdown). BL-1301's reference page
 * reads the full {@link AUCTION_TRANSITION_CATALOG} instead.
 */
export const LIVE_AUCTION_TRANSITIONS: readonly AuctionTransitionMeta[] = Object.values(
  AUCTION_TRANSITION_CATALOG,
).filter((meta) => meta.status === 'live');

/**
 * The operator-facing name for one transition, as read FROM a given phase.
 *
 * The single sanctioned reader of {@link AuctionTransitionMeta.operatorLabel}.
 * Resolving in one place is what keeps BL-1563's RAC 6 true by construction:
 * the dropdown row, the modal heading and the confirm button are all one string
 * downstream of this call, so they cannot drift.
 *
 * **Second implementation, deliberately.** `transitionOptionsFrom`
 * (`transition-options.ts`) inlines the same `?? base` formula because that fold
 * is generic over the phase/id unions and cannot import this auction-specific
 * resolver. If the resolution rule ever gains a second precedence input, change
 * BOTH — grep `operatorLabelByFrom`.
 *
 * @param entry The catalog entry.
 * @param fromPhase The auction's CURRENT phase — the source of the move.
 * @returns The override for `fromPhase` when the entry declares one, else the base label.
 * @sideEffect none — pure.
 */
export function resolveAuctionOperatorLabel(
  entry: AuctionTransitionMeta,
  fromPhase: AuctionPhaseKind,
): string {
  return entry.operatorLabelByFrom?.[fromPhase] ?? entry.operatorLabel;
}
